export type CmsMediaSizeName = 'thumbnail' | 'mobileHero' | 'card' | 'hero';

type CmsMediaSize = {
	url?: string | null;
	width?: number | null;
	height?: number | null;
	filesize?: number | null;
};

export type CmsMediaLike =
	| string
	| {
			url?: string | null;
			alt?: string | null;
			width?: number | null;
			height?: number | null;
			filesize?: number | null;
			mimeType?: string | null;
			filename?: string | null;
			sizes?: Partial<Record<CmsMediaSizeName, CmsMediaSize>> | null;
	  };

function getMediaObject(media: CmsMediaLike | null | undefined) {
	return media && typeof media === 'object' ? media : null;
}

export function getCmsMediaUrl(
	media: CmsMediaLike | null | undefined,
	preferredSizes: CmsMediaSizeName[] = [],
) {
	if (typeof media === 'string') return media;

	const mediaObject = getMediaObject(media);
	if (!mediaObject) return null;

	for (const sizeName of preferredSizes) {
		const size = mediaObject.sizes?.[sizeName];
		const url = size?.url?.trim();
		if (!url) continue;
		// An already-compressed upload can be smaller than a newly encoded hero
		// at identical dimensions. Keep that original; never substitute it for
		// an editor-selected crop or a genuinely resized rendition.
		if (
			sizeName === 'hero' &&
			size?.width &&
			size?.height &&
			size.width === mediaObject.width &&
			size.height === mediaObject.height &&
			size.filesize &&
			mediaObject.filesize &&
			size.filesize >= mediaObject.filesize &&
			mediaObject.url?.trim()
		)
			return mediaObject.url.trim();
		return url;
	}

	return mediaObject.url?.trim() || null;
}

export function getCmsMediaAlt(media: CmsMediaLike | null | undefined, fallback = '') {
	if (typeof media === 'string') return fallback;
	return getMediaObject(media)?.alt?.trim() || fallback;
}

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.m4v', '.ogv'];

/**
 * Detects whether a CMS media asset is a video, by mime type first and
 * falling back to the file extension (mime type is occasionally absent on
 * older uploads). Strings are treated as videos when their path ends in a
 * known video extension.
 */
export function isCmsMediaVideo(media: CmsMediaLike | null | undefined) {
	if (!media) return false;

	const url = typeof media === 'string' ? media : '';
	const mediaObject = getMediaObject(media);
	const mimeType = mediaObject?.mimeType?.trim().toLowerCase();
	if (mimeType) return mimeType.startsWith('video/');

	const source = (mediaObject?.filename || mediaObject?.url || url || '').toLowerCase();
	return VIDEO_EXTENSIONS.some((extension) => source.endsWith(extension));
}
